import { useEffect, useState } from "react";

import { AppSupabaseClient } from "../../types/supabase";
import { useClient } from "./supabase";
import { present } from "../../lib/util/present";
import { useAppSelector } from "../hooks/reduxToolkit";
import { useSentry } from "./sentry";
import React from "react";

interface FeatureFlag { 
  feature: string,
  threshold: number,
  score: number,
}

type FeatureFlagState = Record<string, FeatureFlag>

interface FeatureFlagsContext {
  state: FeatureFlagState
}

const featureFlagsContext = React.createContext<FeatureFlagsContext>({
  state: {}
})

/**
 * Loads feature flags for the current user and provides them to the app.
 */
export function FeatureFlagsProvider({children}: React.PropsWithChildren) {
  const client = useClient()
  const membershipId = useAppSelector(state => state.membership?.membershipId)
  const { captureException } = useSentry()
  
  const [featureFlagState, setFeatureFlagState] = useState<FeatureFlagState>({})
  
  useEffect(() => {
    if (!present(membershipId)) { return }
    
    const load = async () => {
      // load from storage first in case server load fails
      const storageKey = ['featureFlags', membershipId].join(':')
      const storedRaw = localStorage.getItem(storageKey)
      const stored: FeatureFlagState = present(storedRaw) ? JSON.parse(storedRaw) : {}
      setFeatureFlagState(stored)
      
      // try load from server, set if successful
      const newState = await loadFeatureFlags(client, { membershipId })
      
      setFeatureFlagState(newState)
      localStorage.setItem(storageKey, JSON.stringify(newState))
    }
    
    load()
      .catch(error => {
        console.error(error)
        captureException(error)
      })
  }, [client, membershipId, captureException])
  
  return <featureFlagsContext.Provider value={{ state: featureFlagState }}>
    {children}
  </featureFlagsContext.Provider>
}

/**
 * Checks the given feature flag to see if it is enabled for this user.
 * 
 * A feature flag is enabled if the score is greater than or equal to the threshold.
 * This means that the percentage of users that have the feature enabled is equal to the inverse of the threshold.
 * i.e.:
 *   * To enable a feature for a particular user, set their score to 1 in the database.
 *   * To enable a feature for 10% of users, set the threshold to 0.9.
 *   * To enable a feature for 100% of users, set the threshold to 0.
 */
export function useFeature(feature: string) {
  const { state } = React.useContext(featureFlagsContext)
  
  const score = state[feature]?.score ?? 0
  const threshold = state[feature]?.threshold ?? 1
  if (score >= threshold) {
    return true
  }
  return false
}

/**
 * Connects to Supabase to load current feature flag state for the given user.
 */
async function loadFeatureFlags(client: AppSupabaseClient, context: { membershipId: string }) {
  const { data: flags, error: featureFlagsError } = await client.from('feature_flags')
    .select('*')
  if (featureFlagsError) { throw featureFlagsError }
  const { data: membershipFlags, error: membershipFlagsError } = await client.from('membership_feature_flags')
    .select('*')
    .eq('membership_id', context.membershipId)
  if (membershipFlagsError) { throw membershipFlagsError }
  
  const newState: FeatureFlagState = {}
  for (const flag of flags ?? []) {
    let score = membershipFlags?.find(mff => mff.feature === flag.feature)?.score
    if (typeof score != 'number') {
      // insert the feature flag row, returing the auto-generated random score
      const { data: newMembershipFlag, error: newMembershipFlagError } = await client.from('membership_feature_flags')
        .insert({ membership_id: context.membershipId, feature: flag.feature })
        .select('score')
        .single()
      if (newMembershipFlagError) { throw newMembershipFlagError }

      score = newMembershipFlag?.score
    }
    
    newState[flag.feature] = {
      feature: flag.feature,
      threshold: flag.threshold,
      score: score ?? 0
    }
  }

  
  return newState
}
